import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, fmtIDR, fmtDate, printPdf } from "@/lib_app/api";
import InvoiceDetailModal, { INVOICE_STATUS, shortProjectName } from "@/components/app/InvoiceDetailModal";
import ProjectPickerDialog from "@/components/app/ProjectPickerDialog";
import Pagination, { paginate } from "@/components/app/Pagination";
import Avatar from "@/components/app/Avatar";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, FileDown, ExternalLink, Pencil, Search, Loader2, Paperclip, Rows3, LayoutGrid, Printer } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const VIEW_KEY = "sp_invoices_view";

const AGING_LABEL = {
  overdue: "Lewat jatuh tempo",
  not_due: "Belum jatuh tempo",
  d1_30: "Telat 1–30 hari",
  d31_60: "Telat 31–60 hari",
  d61_90: "Telat 61–90 hari",
  d90p: "Telat > 90 hari",
};

/** Days past due (negative = not due yet). */
const daysLate = (dueDate) => {
  if (!dueDate) return 0;
  const ms = new Date(today()).getTime() - new Date(dueDate).getTime();
  return Math.round(ms / 86400000);
};

const matchAging = (r, key) => {
  const st = r.status || "draft";
  if (st !== "issued" && st !== "partial" && st !== "paid") return false;
  const outstanding = Number(r.grand_total || 0) - Number(r.paid_amount || 0);
  if (outstanding <= 0.009) return false;
  const d = r.due_date ? daysLate(r.due_date) : 0;
  switch (key) {
    case "overdue": return !!r.due_date && d > 0;
    case "not_due": return !r.due_date || d <= 0;
    case "d1_30": return d > 0 && d <= 30;
    case "d31_60": return d > 30 && d <= 60;
    case "d61_90": return d > 60 && d <= 90;
    case "d90p": return d > 90;
    default: return true;
  }
};

export default function Invoices() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();

  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [salesFilter, setSalesFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ppnFilter, setPpnFilter] = useState("all");
  const [agingFilter, setAgingFilter] = useState("all");
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState("25");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_KEY) || "table");

  const [pickerOpen, setPickerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalId, setModalId] = useState(null);
  const [modalMode, setModalMode] = useState("read");
  const [modalPrefill, setModalPrefill] = useState(null);

  const canManage = user && (user.role === "owner" || user.role === "finance");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/invoices");
      setRows(r.data || []);
    } catch {
      toast.error("Gagal memuat invoice");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { localStorage.setItem(VIEW_KEY, viewMode); }, [viewMode]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {}); }, []);

  const openCreate = useCallback(async (query) => {
    try {
      const r = await api.get("/invoices/prefill", { params: query });
      setModalPrefill(r.data);
      setModalId(null);
      setModalMode("edit");
      setModalOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Gagal menyiapkan invoice");
    }
  }, []);

  // Deep links: ?quotation=<id> / ?project=<id> (create prefilled) or ?open=<id> (view existing)
  useEffect(() => {
    const quotationId = params.get("quotation");
    const projectId = params.get("project");
    const openId = params.get("open");
    const statusParam = params.get("status");
    const agingParam = params.get("aging");
    if (statusParam || agingParam) {
      if (statusParam && (statusParam === "unpaid" || INVOICE_STATUS[statusParam])) setStatusFilter(statusParam);
      if (agingParam && AGING_LABEL[agingParam]) setAgingFilter(agingParam);
      params.delete("status");
      params.delete("aging");
      setParams(params, { replace: true });
    }
    if (quotationId || projectId) {
      openCreate(quotationId ? { quotation_id: quotationId } : { project_id: projectId }).finally(() => {
        params.delete("quotation");
        params.delete("project");
        setParams(params, { replace: true });
      });
    } else if (openId) {
      openView(openId);
      params.delete("open");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openView = (id) => { setModalPrefill(null); setModalId(id); setModalMode("read"); setModalOpen(true); };
  const openEdit = (id) => { setModalPrefill(null); setModalId(id); setModalMode("edit"); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setModalId(null); setModalPrefill(null); };

  const cycleSort = (key) => {
    if (sortBy !== key) { setSortBy(key); setSortDir("asc"); return; }
    if (sortDir === "asc") { setSortDir("desc"); return; }
    setSortBy(null); setSortDir(null);
  };

  useEffect(() => { setPage(1); }, [q, salesFilter, statusFilter, ppnFilter, agingFilter, pageSize]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (salesFilter !== "all" && r.sales_id !== salesFilter) return false;
      if (statusFilter === "unpaid") {
        if (!["issued", "partial"].includes(r.status || "draft")) return false;
      } else if (statusFilter !== "all" && (r.status || "draft") !== statusFilter) return false;
      if (ppnFilter !== "all" && (r.ppn_type || "ppn") !== ppnFilter) return false;
      if (agingFilter !== "all" && !matchAging(r, agingFilter)) return false;
      if (!s) return true;
      return (r.number || "").toLowerCase().includes(s)
        || (r.customer || "").toLowerCase().includes(s)
        || (r.project_name || "").toLowerCase().includes(s)
        || (r.sales_name || "").toLowerCase().includes(s);
    });
    if (sortBy && sortDir) {
      const val = (r) => {
        switch (sortBy) {
          case "number": return r.number || "";
          case "customer": return (r.customer || "").toLowerCase();
          case "sales": return (r.sales_name || "").toLowerCase();
          case "date": return r.date || "";
          case "due": return r.due_date || "";
          case "total": return Number(r.grand_total || 0);
          default: return "";
        }
      };
      out = [...out].sort((a, b) => {
        const av = val(a), bv = val(b);
        const c = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === "asc" ? c : -c;
      });
    }
    return out;
  }, [rows, q, salesFilter, statusFilter, ppnFilter, agingFilter, sortBy, sortDir]);

  const taxStats = useMemo(() => {
    const sum = (arr) => arr.reduce((a, r) => a + Number(r.grand_total || 0), 0);
    const live = rows.filter((r) => r.status !== "void");
    const ppn = live.filter((r) => (r.ppn_type || "ppn") === "ppn");
    const non = live.filter((r) => (r.ppn_type || "ppn") === "non_ppn");
    return {
      all: { total: sum(live), count: live.length },
      ppn: { total: sum(ppn), count: ppn.length },
      non_ppn: { total: sum(non), count: non.length },
    };
  }, [rows]);

  const voidCount = useMemo(() => rows.filter((r) => r.status === "void").length, [rows]);

  const stats = useMemo(() => {
    const t = today();
    const live = rows.filter((r) => r.status !== "void");
    const scope = ppnFilter === "all" ? live : live.filter((r) => (r.ppn_type || "ppn") === ppnFilter);
    const active = scope.filter((r) => r.status === "issued" || r.status === "partial");
    const paidSum = scope.reduce((a, r) => a + Number(r.paid_amount || 0), 0);
    const totalSum = scope.reduce((a, r) => a + Number(r.grand_total || 0), 0);
    return {
      issuedTotal: active.reduce((a, r) => a + Number(r.grand_total || 0), 0) - active.reduce((a, r) => a + Number(r.paid_amount || 0), 0),
      paidTotal: paidSum,
      totalSum,
      pct: totalSum > 0 ? (paidSum * 100) / totalSum : 0,
      draftCount: scope.filter((r) => r.status === "draft").length,
      overdue: active.filter((r) => r.due_date && r.due_date < t),
    };
  }, [rows, ppnFilter]);

  const { paged, totalPages, curPage, startIdx, endIdx } = paginate(filtered, pageSize, page);

  const remove = async (row) => {
    if (!window.confirm(`Hapus invoice ${row.number}?`)) return;
    try {
      await api.delete(`/invoices/${row.id}`);
      toast.success("Invoice dihapus");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || "Gagal menghapus");
    }
  };

  const downloadPdf = (id) => {
    const token = localStorage.getItem("sp_token");
    window.open(`${API}/invoices/${id}/pdf?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const printInvoicePdf = async (id) => {
    try { await printPdf(`/invoices/${id}/pdf`); } catch { toast.error("Gagal mencetak PDF"); }
  };

  const userById = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);

  return (
    <div className="space-y-6 pb-16" data-testid="invoices-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A0A3A8] mb-2 font-semibold">Invoice</p>
          <h1 className="text-3xl md:text-[34px] tracking-tight font-bold text-[#090808]">Daftar Tagihan</h1>
          <p className="text-sm text-[#A0A3A8] mt-1">Invoice resmi yang diterbitkan dari proyek deal.</p>
        </div>
        <button
          onClick={() => setPickerOpen(true)}
          data-testid="invoice-add-btn"
          className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#090808] text-white text-sm font-semibold hover:bg-black transition-all shadow-[0_8px_20px_rgba(9,8,8,0.18)]"
        >
          <Plus className="w-4 h-4" /> Tambah Invoice
        </button>
      </div>

      {/* Summary — figures follow the active tax filter */}
      {!loading && rows.length > 0 && (
        <div className="bg-white rounded-[22px] p-5 md:p-6 card-soft" data-testid="invoice-summary">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-sm font-bold text-[#090808]">Ringkasan Invoice</h2>
            <span className="text-xs text-[#A0A3A8] font-semibold" data-testid="invoice-summary-count">
              Berdasarkan {taxStats[ppnFilter].count} invoice aktif
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            <div className="rounded-2xl p-4 bg-[#090808] text-white" data-testid="invoice-stat-total">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#B7BAC2]">Total Tagihan</div>
              <div className="text-xl md:text-2xl font-extrabold mt-2 font-num tabular-nums break-words">{fmtIDR(taxStats[ppnFilter].total)}</div>
              <div className="text-[11px] text-[#8A8D96] mt-1.5 font-semibold">Tanpa yang dibatalkan</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]" data-testid="invoice-stat-issued">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#A0A3A8]">Piutang Belum Dibayar</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#090808] break-words">{fmtIDR(stats.issuedTotal)}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Sisa tagihan terbit</div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]" data-testid="invoice-stat-paid">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#A0A3A8]">Sudah Dibayar</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#3EB876] break-words">{fmtIDR(stats.paidTotal)}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Pembayaran tercatat</div>
            </div>
            <div className={`rounded-2xl p-4 border ${stats.overdue.length ? "bg-[#FDE7E9] border-[#FFB3BF]" : "bg-[#FAFBFD] border-[#EEF0F4]"}`} data-testid="invoice-stat-overdue">
              <div className={`text-[10px] uppercase tracking-[0.12em] font-extrabold ${stats.overdue.length ? "text-[#8E4747]" : "text-[#A0A3A8]"}`}>Lewat Jatuh Tempo</div>
              <div className={`text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums break-words ${stats.overdue.length ? "text-[#CE6968]" : "text-[#090808]"}`}>
                {fmtIDR(stats.overdue.reduce((a, r) => a + Number(r.grand_total || 0) - Number(r.paid_amount || 0), 0))}
              </div>
              <div className={`text-[11px] mt-1.5 font-semibold ${stats.overdue.length ? "text-[#8E4747]" : "text-[#A0A3A8]"}`}>
                {stats.overdue.length ? `Dari ${stats.overdue.length} invoice telat` : "Semua tepat waktu"}
              </div>
            </div>
            <div className="rounded-2xl p-4 bg-[#FAFBFD] border border-[#EEF0F4]" data-testid="invoice-stat-draft">
              <div className="text-[10px] uppercase tracking-[0.12em] font-extrabold text-[#A0A3A8]">Draft</div>
              <div className="text-xl md:text-[22px] font-extrabold mt-2 font-num tabular-nums text-[#090808]">{stats.draftCount}</div>
              <div className="text-[11px] text-[#A0A3A8] mt-1.5 font-semibold">Belum diterbitkan</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#F0F1F4] flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="text-xs text-[#626061] font-semibold whitespace-nowrap" data-testid="invoice-progress-label">
              Progres pembayaran: <b className="text-[#090808]">{fmtIDR(stats.paidTotal)}</b> / {fmtIDR(stats.totalSum)}
            </div>
            <div className="flex-1 min-w-[140px] h-2.5 bg-[#EEF0F4] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#57D28A] to-[#2FA968] rounded-full transition-all" style={{ width: `${Math.min(100, stats.pct)}%` }} />
            </div>
            <div className="text-sm font-extrabold text-[#2FA968] tabular-nums" data-testid="invoice-progress-pct">{stats.pct.toFixed(1).replace(".", ",")}%</div>
          </div>
        </div>
      )}

      {/* Tax tabs (Semua / PPN / Non-PPN / Dibatalkan) + view toggle */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="invoice-tax-toggle">
        {[
          { key: "all", label: "Semua", s: taxStats.all, color: "bg-[#090808] text-white" },
          { key: "ppn", label: "PPN 11%", s: taxStats.ppn, color: "bg-[#3B4A63] text-white" },
          { key: "non_ppn", label: "Non-PPN", s: taxStats.non_ppn, color: "bg-[#626061] text-white" },
        ].map((t) => {
          const active = ppnFilter === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setPpnFilter(t.key)}
              data-testid={`invoice-tax-toggle-${t.key}`}
              className={`inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold transition-all ${active ? `${t.color} shadow-[0_4px_12px_rgba(0,0,0,0.10)]` : "bg-white text-[#626061] hover:bg-[#F4F5F7] card-soft"}`}
            >
              {t.label}
              <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold ${active ? "bg-white/25 text-white" : "bg-[#F4F5F7] text-[#626061]"}`}>{t.s.count}</span>
            </button>
          );
        })}
        {voidCount > 0 && (
          statusFilter === "void" ? (
            <button
              onClick={() => setStatusFilter("all")}
              data-testid="invoice-void-hide"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold bg-[#CE6968] text-white shadow-[0_4px_12px_rgba(0,0,0,0.10)]"
            >
              Dibatalkan
              <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-white/25 text-white">{voidCount}</span>
            </button>
          ) : (
            <button
              onClick={() => setStatusFilter("void")}
              data-testid="invoice-void-show"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-full text-sm font-semibold bg-white text-[#626061] hover:bg-[#F4F5F7] card-soft"
            >
              Dibatalkan
              <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-[#FDE7E9] text-[#CE6968]">{voidCount}</span>
            </button>
          )
        )}
        </div>
        <div className="inline-flex bg-white card-soft rounded-full p-1 gap-1">
          <button
            data-testid="invoice-view-table"
            onClick={() => setViewMode("table")}
            className={`h-9 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
              viewMode === "table" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
            }`}
            title="Tampilan Tabel"
          >
            <Rows3 className="w-3.5 h-3.5" /> Tabel
          </button>
          <button
            data-testid="invoice-view-card"
            onClick={() => setViewMode("card")}
            className={`h-9 px-3 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors ${
              viewMode === "card" ? "bg-[#090808] text-white" : "text-[#626061] hover:bg-[#F5F3FF]"
            }`}
            title="Tampilan Kartu"
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Kartu
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A8]" />
          <Input
            data-testid="invoice-search"
            placeholder="Cari nomor / pelanggan / proyek..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-11 h-12 rounded-full bg-white border-transparent card-soft focus-visible:ring-[#090808]/40"
          />
        </div>
        <Select value={salesFilter} onValueChange={setSalesFilter}>
          <SelectTrigger data-testid="invoice-sales-filter" className="w-52 h-12 rounded-full bg-white card-soft border-transparent px-5"><SelectValue placeholder="Tim" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tim</SelectItem>
            {users.filter((u) => u.is_active !== false && u.role !== "owner" && u.role !== "finance").map((u) => (
              <SelectItem key={u.user_id} value={u.user_id}>
                <div className="flex items-center gap-2">
                  <Avatar name={u.name} picture={u.picture} size="sm" className="!w-5 !h-5 !text-[9px] !border-0" />
                  <span className="text-sm font-semibold">{u.nickname || u.name}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="invoice-status-filter" className="w-44 h-12 rounded-full bg-white card-soft border-transparent px-5"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            <SelectItem value="unpaid">Belum Lunas</SelectItem>
            {Object.entries(INVOICE_STATUS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {agingFilter !== "all" && (
        <div className="flex items-center gap-2" data-testid="invoice-aging-chip">
          <span className="text-xs text-[#626061] font-semibold">Filter umur piutang:</span>
          <button
            onClick={() => setAgingFilter("all")}
            data-testid="invoice-aging-clear"
            className="inline-flex items-center gap-2 h-8 px-4 rounded-full bg-[#E5EEFA] text-[#3B4A63] border border-[#B0C7EA] text-xs font-bold hover:bg-[#D3E1F5] transition-colors"
          >
            {AGING_LABEL[agingFilter]} <span className="text-[#8A8D96]">✕</span>
          </button>
        </div>
      )}

      {loading && <div className="text-sm text-[#A0A3A8]">Memuat invoice... <Loader2 className="w-3.5 h-3.5 animate-spin inline" /></div>}

      {!loading && filtered.length === 0 && (
        <div className="bg-white rounded-2xl py-16 text-center text-sm text-[#A0A3A8] card-soft" data-testid="invoice-empty">
          Belum ada invoice. Klik <span className="font-semibold text-[#090808]">Tambah Invoice</span> lalu pilih proyek yang sudah deal.
        </div>
      )}

      {!loading && filtered.length > 0 && viewMode === "table" && (
        <div className="bg-white rounded-2xl card-soft overflow-hidden" data-testid="invoices-table">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px]">
              <thead className="bg-[#F4F5F7]">
                <tr className="text-left text-[10px] uppercase tracking-widest text-[#626061] font-bold">
                  <th className="px-5 py-3"><SortTh label="No. Invoice" sortKey="number" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                  <th className="px-5 py-3"><SortTh label="Pelanggan" sortKey="customer" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                  <th className="px-5 py-3"><SortTh label="Tim" sortKey="sales" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                  <th className="px-5 py-3"><SortTh label="Tanggal" sortKey="date" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} /></th>
                  <th className="px-5 py-3 text-right"><SortTh label="Total Tagihan" sortKey="total" sortBy={sortBy} sortDir={sortDir} onClick={cycleSort} align="right" /></th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const u = userById[r.sales_id];
                  const st = INVOICE_STATUS[r.status] || INVOICE_STATUS.draft;
                  const overdue = (r.status === "issued" || r.status === "partial") && r.due_date && r.due_date < today();
                  return (
                    <tr
                      key={r.id}
                      data-testid={`invoice-row-${r.id}`}
                      onClick={() => openView(r.id)}
                      className="border-t border-[#F4F5F7] hover:bg-[#FAFBFD] cursor-pointer transition-colors"
                    >
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[13px] font-num font-bold text-[#090808] tabular-nums">{r.number}</span>
                          {(r.attachments_count || 0) > 0 && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#3B4A63] bg-[#E5EEFA] rounded-full px-1.5 h-4"
                              title={`${r.attachments_count} dokumen invoice`}
                              data-testid={`invoice-att-count-${r.id}`}
                            >
                              <Paperclip className="w-2.5 h-2.5" /> {r.attachments_count}
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold mt-0.5">{r.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm font-bold text-[#090808]">{r.customer}</div>
                        <div className="text-xs text-[#626061] truncate max-w-[220px]">{shortProjectName(r.project_name, r.customer) || "-"}</div>
                        {r.termin_enabled && (
                          <span className="inline-flex items-center h-5 px-2 mt-1 rounded-full text-[10px] font-bold bg-[#E5EEFA] text-[#3B4A63] border border-[#B0C7EA]" data-testid={`invoice-termin-${r.id}`}>
                            {(r.termin_label || "Termin")} {Number(r.termin_percent || 0)}%
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <Avatar name={r.sales_name} picture={u?.picture} size="sm" className="!w-6 !h-6 !text-[10px]" />
                          <span className="text-xs font-semibold text-[#090808]">{u?.nickname || r.sales_name || "-"}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Terbit</span>
                          <span className="text-xs tabular-nums text-[#3B4A63] font-semibold">{r.date ? fmtDate(r.date) : "-"}</span>
                        </div>
                        <div className="flex items-baseline gap-1.5 mt-0.5">
                          <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Tempo</span>
                          <span className={`text-xs tabular-nums font-semibold ${overdue ? "text-[#CE6968]" : "text-[#626061]"}`} data-testid={`invoice-due-${r.id}`}>
                            {r.due_date ? fmtDate(r.due_date) : "-"}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        <div className="text-sm font-num font-bold text-[#090808] tabular-nums">{fmtIDR(r.grand_total)}</div>
                        <div className="text-[11px] text-[#A0A3A8] font-num tabular-nums">
                          {r.termin_enabled ? `Ditagih ${fmtIDR(r.billed_amount)}` : `Dasar ${fmtIDR(r.subtotal || 0)}`}
                        </div>
                        {Number(r.paid_amount || 0) > 0 && (
                          <div className="text-[11px] font-num tabular-nums mt-0.5" data-testid={`invoice-paid-${r.id}`}>
                            <span className="text-[#3EB876] font-semibold">{fmtIDR(r.paid_amount)}</span>
                            <span className="text-[#A0A3A8]"> · sisa </span>
                            <span className="text-[#CE6968] font-semibold">{fmtIDR(Math.max(0, Number(r.grand_total || 0) - Number(r.paid_amount || 0)))}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${st.chip}`} data-testid={`invoice-status-${r.id}`}>
                          {st.label}
                        </span>
                        {overdue && (
                          <div className="text-[10px] font-bold text-[#CE6968] mt-1 uppercase tracking-widest">Jatuh tempo</div>
                        )}
                      </td>
                      <td className="px-5 py-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1.5">
                          <IconBtn title="Unduh PDF" testid={`invoice-pdf-${r.id}`} onClick={() => downloadPdf(r.id)} tone="green"><FileDown className="w-4 h-4" /></IconBtn>
                          <IconBtn title="Cetak PDF" testid={`invoice-print-${r.id}`} onClick={() => printInvoicePdf(r.id)} tone="blue"><Printer className="w-4 h-4" /></IconBtn>
                          {r.project_id && (
                            <IconBtn title="Buka Proyek" testid={`invoice-project-${r.id}`} onClick={() => navigate(`/projects?open=${r.project_id}`)} tone="blue"><ExternalLink className="w-4 h-4" /></IconBtn>
                          )}
                          <IconBtn title="Edit" testid={`invoice-edit-${r.id}`} onClick={() => openEdit(r.id)} tone="slate"><Pencil className="w-4 h-4" /></IconBtn>
                          {canManage && r.status === "draft" && (
                            <IconBtn title="Hapus" testid={`invoice-delete-${r.id}`} onClick={() => remove(r)} tone="red"><Trash2 className="w-4 h-4" /></IconBtn>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && viewMode === "card" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="invoices-card-grid">
          {paged.map((r) => (
            <InvoiceCard
              key={r.id}
              row={r}
              user={userById[r.sales_id]}
              canDelete={canManage && r.status === "draft"}
              onOpen={() => openView(r.id)}
              onEdit={() => openEdit(r.id)}
              onPdf={() => downloadPdf(r.id)}
              onPrint={() => printInvoicePdf(r.id)}
              onProject={r.project_id ? () => navigate(`/projects?open=${r.project_id}`) : null}
              onDelete={() => remove(r)}
            />
          ))}
        </div>
      )}

      {filtered.length > 0 && (
        <Pagination
          total={filtered.length}
          page={curPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onPageChange={setPage}
          startIdx={startIdx}
          endIdx={endIdx}
          label="invoice"
          testidPrefix="invoice-pagination"
        />
      )}

      <ProjectPickerDialog
        key={pickerOpen ? "open" : "closed"}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={({ project, quotationId }) => {
          setPickerOpen(false);
          openCreate(quotationId ? { project_id: project.id, quotation_id: quotationId } : { project_id: project.id });
        }}
      />

      <InvoiceDetailModal
        open={modalOpen}
        invoiceId={modalId}
        prefill={modalPrefill}
        initialMode={modalMode}
        onClose={closeModal}
        onSaved={() => load()}
        onDeleted={() => load()}
      />
    </div>
  );
}

const TONES = {
  green: "text-[#3f7a52] bg-[#E4F9EC]/70 border-[#B7E7C7] hover:bg-[#D8EEDF]",
  blue: "text-[#3B4A63] bg-[#E5EEFA]/70 border-[#B0C7EA] hover:bg-[#D3E1F5]",
  slate: "text-[#626061] bg-[#F4F5F7] border-[#DCE0E7] hover:bg-[#E9EBEF]",
  red: "text-[#CE6968] bg-[#FDE7E9]/70 border-[#FFB3BF] hover:bg-[#FBD6DA]",
};

function IconBtn({ title, testid, onClick, tone, children }) {
  return (
    <button
      title={title}
      data-testid={testid}
      onClick={onClick}
      className={`w-9 h-9 rounded-full border inline-flex items-center justify-center transition-all active:scale-95 ${TONES[tone]}`}
    >
      {children}
    </button>
  );
}

const CARD_TINT = {
  draft:   { bg: "linear-gradient(160deg, rgba(226,229,236,0.48) 0%, rgba(240,242,246,0.30) 100%)", border: "rgba(160,163,168,0.32)" },
  issued:  { bg: "linear-gradient(160deg, rgba(190,210,240,0.44) 0%, rgba(210,222,242,0.28) 100%)", border: "rgba(150,175,220,0.35)" },
  partial: { bg: "linear-gradient(160deg, rgba(255,231,150,0.46) 0%, rgba(255,243,203,0.28) 100%)", border: "rgba(226,190,80,0.35)" },
  paid:    { bg: "linear-gradient(160deg, rgba(180,240,205,0.44) 0%, rgba(200,246,220,0.28) 100%)", border: "rgba(120,200,150,0.35)" },
  void:    { bg: "linear-gradient(160deg, rgba(255,193,203,0.34) 0%, rgba(255,213,220,0.22) 100%)", border: "rgba(255,140,155,0.32)" },
};

function InvoiceCard({ row: r, user, canDelete, onOpen, onEdit, onPdf, onPrint, onProject, onDelete }) {
  const st = INVOICE_STATUS[r.status] || INVOICE_STATUS.draft;
  const overdue = (r.status === "issued" || r.status === "partial") && r.due_date && r.due_date < today();
  const tint = CARD_TINT[r.status] || CARD_TINT.draft;
  const paid = Number(r.paid_amount || 0);
  const total = Number(r.grand_total || 0);
  const remaining = Math.max(0, total - paid);
  const pct = total > 0 ? (paid * 100) / total : 0;

  return (
    <div
      data-testid={`invoice-card-${r.id}`}
      onClick={onOpen}
      className={`group relative rounded-2xl cursor-pointer transition-shadow hover:shadow-[0_10px_28px_rgba(24,27,51,0.14),0_24px_60px_rgba(24,27,51,0.10)] ${r.status === "void" ? "opacity-80" : ""}`}
      style={{
        background: tint.bg,
        backdropFilter: "blur(24px) saturate(160%)",
        WebkitBackdropFilter: "blur(24px) saturate(160%)",
        border: `1px solid ${tint.border}`,
        boxShadow: "0 10px 30px -12px rgba(20,22,31,0.12), inset 0 1px 0 rgba(255,255,255,0.55)",
      }}
    >
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-[13px] font-num font-bold text-[#090808] tabular-nums truncate">{r.number}</span>
              {(r.attachments_count || 0) > 0 && (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-[#3B4A63] bg-white/70 rounded-full px-1.5 h-4 shrink-0"
                  title={`${r.attachments_count} dokumen invoice`}
                  data-testid={`invoice-card-att-${r.id}`}
                >
                  <Paperclip className="w-2.5 h-2.5" /> {r.attachments_count}
                </span>
              )}
            </div>
            <div className="text-base font-bold text-[#090808] leading-tight truncate mt-1">{r.customer}</div>
            <div className="text-xs text-[#626061] truncate mt-0.5">{shortProjectName(r.project_name, r.customer) || "-"}</div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <span className={`inline-flex items-center h-6 px-2.5 rounded-full text-[10px] font-bold border whitespace-nowrap ${st.chip}`} data-testid={`invoice-card-status-${r.id}`}>
              {st.label}
            </span>
            <span className="text-[9px] uppercase tracking-widest text-[#626061] font-bold">{r.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}</span>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Avatar name={r.sales_name} picture={user?.picture} size="sm" className="!w-6 !h-6 !text-[10px]" />
          <span className="text-xs font-semibold text-[#090808]">{user?.nickname || r.sales_name || "-"}</span>
          {r.termin_enabled && (
            <span className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold bg-white/70 text-[#3B4A63] border border-[#B0C7EA]" data-testid={`invoice-card-termin-${r.id}`}>
              {(r.termin_label || "Termin")} {Number(r.termin_percent || 0)}%
            </span>
          )}
        </div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#626061] font-semibold">Tagihan</div>
            <div className="font-num text-sm font-bold text-[#090808] tabular-nums leading-tight mt-0.5">{fmtIDR(total)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#4A6D53] font-semibold">Terbayar</div>
            <div className="font-num text-sm font-bold text-[#3EB876] tabular-nums leading-tight mt-0.5">{fmtIDR(paid)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#CE6968] font-semibold">Sisa</div>
            <div className="font-num text-sm font-bold text-[#CE6968] tabular-nums leading-tight mt-0.5">{fmtIDR(remaining)}</div>
          </div>
        </div>

        <div className="mt-2.5 h-1.5 rounded-full bg-white/60 overflow-hidden">
          <div className="h-full bg-[#57D28A]" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px]">
          <div className="flex items-baseline gap-1.5">
            <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Terbit</span>
            <span className="tabular-nums text-[#3B4A63] font-semibold">{r.date ? fmtDate(r.date) : "-"}</span>
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[9px] uppercase tracking-widest text-[#A0A3A8] font-bold">Tempo</span>
            <span className={`tabular-nums font-semibold ${overdue ? "text-[#CE6968]" : "text-[#626061]"}`} data-testid={`invoice-card-due-${r.id}`}>
              {r.due_date ? fmtDate(r.due_date) : "-"}
            </span>
          </div>
        </div>

        <div
          className="mt-3 pt-3 border-t border-white/60 flex items-center justify-between gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {overdue ? (
            <span className="text-[10px] font-bold text-[#CE6968] uppercase tracking-widest">Lewat jatuh tempo</span>
          ) : (
            <span className="text-[10px] font-bold text-[#A0A3A8] uppercase tracking-widest">{r.status === "paid" ? "Lunas" : "Aksi cepat"}</span>
          )}
          <div className="flex items-center gap-1.5">
            <IconBtn title="Unduh PDF" testid={`invoice-card-pdf-${r.id}`} onClick={onPdf} tone="green"><FileDown className="w-4 h-4" /></IconBtn>
            <IconBtn title="Cetak PDF" testid={`invoice-card-print-${r.id}`} onClick={onPrint} tone="blue"><Printer className="w-4 h-4" /></IconBtn>
            {onProject && (
              <IconBtn title="Buka Proyek" testid={`invoice-card-project-${r.id}`} onClick={onProject} tone="blue"><ExternalLink className="w-4 h-4" /></IconBtn>
            )}
            <IconBtn title="Edit" testid={`invoice-card-edit-${r.id}`} onClick={onEdit} tone="slate"><Pencil className="w-4 h-4" /></IconBtn>
            {canDelete && (
              <IconBtn title="Hapus" testid={`invoice-card-delete-${r.id}`} onClick={onDelete} tone="red"><Trash2 className="w-4 h-4" /></IconBtn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SortGlyph({ dir }) {
  const upOp = dir === "desc" ? 0.25 : dir === "asc" ? 1 : 0.55;
  const downOp = dir === "asc" ? 0.25 : dir === "desc" ? 1 : 0.55;
  return (
    <svg width="8" height="11" viewBox="0 0 8 11" aria-hidden="true" className="shrink-0">
      <path d="M4 0.5L7.5 4.2H0.5L4 0.5Z" fill="currentColor" opacity={upOp} />
      <path d="M4 10.5L0.5 6.8H7.5L4 10.5Z" fill="currentColor" opacity={downOp} />
    </svg>
  );
}

function SortTh({ label, sortKey, sortBy, sortDir, onClick, align = "left" }) {
  const active = sortBy === sortKey;
  return (
    <button
      type="button"
      onClick={() => onClick(sortKey)}
      data-testid={`invoice-sort-${sortKey}`}
      className={`inline-flex items-center gap-1.5 uppercase tracking-widest hover:text-[#090808] transition-colors ${active ? "text-[#090808]" : ""}`}
      style={{ justifyContent: align === "right" ? "flex-end" : "flex-start" }}
    >
      {align === "right" && <SortGlyph dir={active ? sortDir : null} />}
      <span>{label}</span>
      {align !== "right" && <SortGlyph dir={active ? sortDir : null} />}
    </button>
  );
}
